import { useTheme, THEME_COLORS } from "../contexts/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Palette, Check, Sparkles } from "lucide-react";
import toast from "react-hot-toast";

export default function ThemeCustomizationPage() {
  const { themeColor, changeThemeColor, theme } = useTheme();

  const handleColorChange = (colorKey) => {
    changeThemeColor(colorKey);
    toast.success(`تم تغيير اللون إلى ${THEME_COLORS[colorKey].name}`, {
      icon: '🎨',
      style: {
        background: THEME_COLORS[colorKey].hex,
        color: '#fff',
      },
    });
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="theme-customization-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${theme.gradient} flex items-center justify-center`}>
          <Palette className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">
            تخصيص الألوان
          </h1>
          <p className="text-slate-400">اختر اللون المفضل لتطبيقك</p>
        </div>
      </div>

      {/* Color Picker */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Sparkles className={`w-5 h-5 ${theme.text}`} />
            اختر اللون الرئيسي
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {Object.entries(THEME_COLORS).map(([key, color]) => (
              <button
                key={key}
                onClick={() => handleColorChange(key)}
                className={`relative group p-4 rounded-xl border-2 transition-all duration-300 hover:scale-105 ${
                  themeColor === key 
                    ? `border-white shadow-lg shadow-${color.primary}-500/30` 
                    : 'border-slate-700 hover:border-slate-500'
                }`}
              >
                {/* Color Circle */}
                <div 
                  className={`w-12 h-12 rounded-full mx-auto mb-3 bg-gradient-to-br ${color.gradient} shadow-lg`}
                  style={{ boxShadow: `0 4px 20px ${color.hex}40` }}
                />
                
                {/* Color Name */}
                <p className="text-white text-sm font-medium text-center">
                  {color.name}
                </p>
                
                {/* Selected Check */}
                {themeColor === key && (
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <Check className="w-4 h-4 text-slate-900" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">معاينة</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-slate-400">هكذا ستظهر العناصر في التطبيق:</p>
          
          <div className="flex flex-wrap gap-3">
            <Button className={`bg-gradient-to-r ${theme.gradient}`}>
              زر رئيسي
            </Button>
            <Button variant="outline" className={`${theme.border} ${theme.text}`}>
              زر ثانوي
            </Button>
            <div className={`px-4 py-2 rounded-full ${theme.bg}/20 ${theme.text}`}>
              شارة
            </div>
          </div>
          
          <div className="flex items-center gap-3 p-4 bg-slate-900/50 rounded-lg">
            <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${theme.gradient} flex items-center justify-center`}>
              <Palette className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-medium">عنصر مثال</p>
              <p className={`text-sm ${theme.text}`}>وصف العنصر</p>
            </div>
          </div>
          
          {/* Progress Bar Example */}
          <div className="space-y-2">
            <p className="text-sm text-slate-400">شريط التقدم:</p>
            <div className="w-full h-3 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className={`h-full bg-gradient-to-r ${theme.gradient} rounded-full transition-all duration-500`}
                style={{ width: '70%' }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="py-4">
          <p className="text-slate-400 text-sm text-center">
            💡 سيتم حفظ اختيارك تلقائياً وتطبيقه على جميع صفحات التطبيق
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
